import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { peticion } from '../api/cliente.js';
import type { RespuestaDeHuecos, Servicio } from '../api/tipos.js';
import { Aviso, BotonAtras, Cargando, EstadoVacio } from '../componentes/comunes.js';
import { comoInstante, fechaLarga, fechaParaCalendario, ventanaDeDias } from '../fechas.js';
import { nombreDeProducto } from '../nombres.js';
import { textos } from '../textos.js';

/**
 * Paso 4: elegir día y hora (§9).
 *
 * Todas las horas se consultan y muestran en el horario de Madrid. El aviso
 * visible evita que el cliente interprete la hora como la de su dispositivo.
 */
export function PantallaHora({
  servicio,
  zona,
  alElegir,
  alVolver,
  avisoDeConflicto,
}: {
  servicio: Servicio;
  zona: string;
  alElegir: (instanteIso: string) => void;
  alVolver: () => void;
  /** Se enseña cuando el hueco elegido se ocupó mientras el cliente confirmaba. */
  avisoDeConflicto?: string | null;
}) {
  const [semana, setSemana] = useState(0);
  const [fechaSeleccionada, setFechaSeleccionada] = useState<string | null>(null);
  const rango = ventanaDeDias(semana, zona);

  const consulta = useQuery({
    queryKey: ['huecos', servicio.id, rango.desde, rango.hasta, zona],
    queryFn: () =>
      peticion<RespuestaDeHuecos>(
        `/servicios/${servicio.id}/huecos?desde=${rango.desde}&hasta=${rango.hasta}&tz=${encodeURIComponent(zona)}`,
      ),
    // La disponibilidad envejece rápido: si el cliente vuelve a esta pantalla,
    // se pide de nuevo en vez de enseñarle una foto vieja.
    staleTime: 0,
  });

  const dias = consulta.data?.dias ?? [];
  const diasDisponibles = dias.filter((dia) => dia.huecos.length > 0);
  // Si cambia la semana o la zona, una selección que ya no existe cae de forma
  // natural en el primer día disponible, sin un efecto ni un render intermedio.
  const diaSeleccionado =
    diasDisponibles.find((dia) => dia.fecha === fechaSeleccionada) ?? diasDisponibles[0] ?? null;

  return (
    <section className="pantalla-hora" aria-labelledby="titulo-hora">
      <BotonAtras alClick={alVolver} />
      <h2 id="titulo-hora">{textos.hora.titulo}</h2>
      <p className="ayuda">{nombreDeProducto(servicio.nombre)}</p>

      {avisoDeConflicto !== undefined && avisoDeConflicto !== null && (
        <Aviso tipo="atencion">{avisoDeConflicto}</Aviso>
      )}

      <Aviso tipo="informacion" titulo={textos.hora.avisoZonaTitulo}>
        {textos.hora.avisoZonaDetalle}
      </Aviso>

      {consulta.isPending && <Cargando />}
      {consulta.isError && (
        <Aviso tipo="error">
          {textos.errores.generico}{' '}
          <button type="button" className="enlace" onClick={() => void consulta.refetch()}>
            {textos.aplicacion.reintentar}
          </button>
        </Aviso>
      )}

      {consulta.data !== undefined && (
        <div className="selector-cita">
          <div className="cabecera-selector-cita">
            <div>
              <span>{textos.hora.disponibilidad}</span>
              <strong>
                {fechaLarga(rango.desde, zona)} — {fechaLarga(rango.hasta, zona)}
              </strong>
            </div>
            <nav className="navegacion-semanas" aria-label={textos.hora.disponibilidad}>
              <button
                type="button"
                aria-label={textos.hora.semanaAnterior}
                onClick={() => setSemana((actual) => Math.max(0, actual - 1))}
                disabled={semana === 0}
              >
                <span aria-hidden="true">←</span>
              </button>
              <button
                type="button"
                aria-label={textos.hora.semanaSiguiente}
                onClick={() => setSemana((actual) => actual + 1)}
              >
                <span aria-hidden="true">→</span>
              </button>
            </nav>
          </div>

          {diasDisponibles.length === 0 ? (
            <EstadoVacio
              titulo={textos.hora.sinHuecosTitulo}
              detalle={textos.hora.sinHuecos}
              compacto
            />
          ) : (
            <>
              <ul className="dias-disponibles" aria-label={textos.hora.diasDisponibles}>
                {diasDisponibles.map((dia) => {
                  const partes = fechaParaCalendario(dia.fecha, zona);
                  const etiqueta = fechaLarga(dia.fecha, zona);
                  const activo = dia.fecha === diaSeleccionado?.fecha;
                  return (
                    <li key={dia.fecha}>
                      <button
                        type="button"
                        className="dia-calendario"
                        aria-label={`${textos.hora.verDia(etiqueta)}. ${textos.hora.huecosDisponibles(dia.huecos.length)}`}
                        aria-pressed={activo}
                        onClick={() => setFechaSeleccionada(dia.fecha)}
                      >
                        <span className="dia-semana">{partes.semana}</span>
                        <strong>{partes.dia}</strong>
                        <span className="dia-mes">{partes.mes}</span>
                        <small>{dia.huecos.length}</small>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {diaSeleccionado !== null && (
                <div className="panel-horas">
                  <div className="cabecera-horas">
                    <div>
                      <span>{textos.hora.eligeUnaHora}</span>
                      <h3>{fechaLarga(diaSeleccionado.fecha, zona)}</h3>
                    </div>
                    <p>{textos.hora.eligeUnaHoraAyuda}</p>
                  </div>
                  <ul
                    className="horas"
                    aria-label={textos.hora.huecosDe(fechaLarga(diaSeleccionado.fecha, zona))}
                  >
                    {diaSeleccionado.huecos.map((hora) => (
                      <li key={hora}>
                        <button
                          type="button"
                          className="hora"
                          aria-label={textos.hora.elegirHora(
                            hora,
                            fechaLarga(diaSeleccionado.fecha, zona),
                          )}
                          onClick={() => alElegir(comoInstante(diaSeleccionado.fecha, hora, zona))}
                        >
                          {hora}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="botonera">
        <button type="button" className="enlace" onClick={alVolver}>
          {textos.aplicacion.volver}
        </button>
      </div>
    </section>
  );
}
